import { scheduleWindows } from '@verlo/shared/lib/promotions';

// Promotions' four lists (Back office sweep, Oct 2026: D9, mock "Promotions"). Live is
// inside its dates, running now or not; Scheduled starts on a later date; Drafts; Ended
// (ended, or past its last date).
//
// All of it is read as the till reads a schedule: on the venue's own clock (`venueNow`) and
// its trading day, which opens at `dayStart` (05:00 by default), so 00:40 on Saturday is
// still Friday's trading day. The server gives `today` (the trading date), the timezone and
// the day start (`GET /promotions/usage`).

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mins = (hhmm) => { const [h, m] = String(hhmm || '00:00').split(':').map(Number); return h * 60 + m; };

// The venue's wall clock now, as a Date whose fields read the venue's time.
export function venueNow(tz, now = new Date()) {
  if (!tz) return now;
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(now).map((x) => [x.type, x.value]));
    return new Date(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  } catch {
    return now;
  }
}

// The trading date a moment on the venue's clock belongs to: before the day opens, it's yesterday's.
export function tradingDate(at, dayStart = '05:00') {
  const d = new Date(at.getFullYear(), at.getMonth(), at.getDate());
  if (at.getHours() * 60 + at.getMinutes() < mins(dayStart)) d.setDate(d.getDate() - 1);
  return d;
}

export function listOf(p, today) {
  const s = p.schedule || {};
  if (p.status === 'draft') return 'drafts';
  if (p.status === 'ended' || (s.end && s.end < today)) return 'ended';
  if (s.start && s.start > today) return 'scheduled';
  return 'live';
}

// When it next starts after `now` (venue clock): the first window, on a trading day it runs
// inside its dates. A window starting before the day opens (00:30) is the next calendar morning.
export function nextStart(p, now = new Date(), dayStart = '05:00') {
  const s = p.schedule || {};
  const first = tradingDate(now, dayStart);
  let best = null;
  for (let i = 0; i <= 7; i += 1) {
    const tday = new Date(first.getFullYear(), first.getMonth(), first.getDate() + i);
    const key = ymd(tday);
    if ((s.start && key < s.start) || (s.end && key > s.end)) continue;
    for (const w of scheduleWindows(s)) {
      const days = (w.days || []).map(Number);
      if (days.length && !days.includes((tday.getDay() + 6) % 7)) continue;
      const t = mins(w.time_start || dayStart);
      const at = new Date(tday.getFullYear(), tday.getMonth(), tday.getDate(), Math.floor(t / 60), t % 60);
      if (t < mins(dayStart)) at.setDate(at.getDate() + 1);
      if (at > now && (!best || at < best.at)) best = { at, allDay: !w.time_start };
    }
    if (best) return best;
  }
  return null;
}

// What it's doing right now: "On now", "Paused", "Starts 17:00 today", "Next: Mon 16:00".
export function rightNow(p, now = new Date(), runningIds = new Set(), dayStart = '05:00') {
  if (p.status === 'paused') return { words: 'Paused', tone: 'warn' };
  if (p.status === 'live' && runningIds.has(p.id)) return { words: 'On now', tone: 'ok' };
  const next = nextStart(p, now, dayStart);
  if (!next) return { words: 'Not again', tone: null };
  const time = `${String(next.at.getHours()).padStart(2, '0')}:${String(next.at.getMinutes()).padStart(2, '0')}`;
  if (ymd(tradingDate(next.at, dayStart)) === ymd(tradingDate(now, dayStart))) {
    return { words: next.allDay ? 'Later today' : `Starts ${time} today`, tone: null };
  }
  const day = next.at.toLocaleDateString('en-GB', { weekday: 'short' });
  const far = (next.at - now) / 86400000 > 6 ? ` ${next.at.getDate()} ${next.at.toLocaleDateString('en-GB', { month: 'short' })}` : '';
  return { words: `Next: ${day}${far}${next.allDay ? '' : ` ${time}`}`, tone: null };
}
